/* eslint-disable max-len */
import StringConstants from '@common/constants/StringConstants';
import { Anchor, Text } from '@zillow/constellation-10';

export default {
  ONBOARDING_V2: {
    RENT_AMOUNT_PAGE: [
      {
        question: 'How do I collect rent payments?',
        answer: `Payments are deposited directly into your checking account.
            We notify you each time your tenant makes a payment.`,
      },
      {
        question: 'What if I need to collect prorated rent or a security deposit?',
        answer: `In a moment, you'll be able to set up the move-in costs you want the renter to pay,
            including security deposit, prorated rent, and any other fees.`,
      },
      {
        question: 'Does your service cost anything?',
        answer: `Our services are currently free to landlords.
            They’re also free to tenants who pay rent directly from their bank account.
            Tenants who pay using a debit/credit card may be subject to a fee.`,
      },
    ],
    DUE_DATE_PAGE: [
      {
        question: 'What is the monthly payment date?',
        answer: `We’ll ask your renter to send their monthly rent on or
            before this day. Once they do, it typically takes 5
            business days for the funds to get deposited into your
            account.`,
      },
      {
        question: 'Why do payments typically take 5 business days to get deposited?',
        answer: `Because a secure banking network must validate and
            verify each transfer. Similar to depositing a check, that
            process takes a few days, and weekends and holidays
            can extend the time. Zillow does not access the funds
            as they are being transferred, and we ensure that our
            trusted processing partners employ strong security
            standards.`,
      },
    ],
    TERM_LENGTH_PAGE: [
      {
        question: 'What if my tenant is already paying rent?',
        answer: `No problem. Simply select the first date you'd like them to pay rent using
            our service. Talk with your tenant about the change, and allow them time to set up their
            account before that first payment.`,
      },
      {
        question: 'Should this match the start and end dates of my rental agreement?',
        answer: `Not necessarily. Here you're setting the duration of time you'd like to
            collect rent using our service. This may differ from the duration of your rental
            agreement. For instance, your payment start date may not match your rental agreement
            if your renter already pays rent another way.`,
      },
    ],
    ACCOUNT_INFO_PAGE: [
      {
        question: 'How long does it take for payments to get deposited?',
        answer: `Typically, it takes 5 business days from when
            your renter sends their payment. The first
            payment may take an additional 3 business
            days while we verify your renter’s bank
            account.`,
      },
      {
        question: 'How do you keep my information secure?',
        answer: `We take the security of our user's information seriously. We intentionally limit
            the amount of sensitive data we store, and we use multiple layers of security to keep your
            data safe. Additionally, we continually review and update our practices to stay on top
            evolving threats.`,
      },
    ],
    INVITE_TENANT_PAGE: [
      {
        question: 'Can I set up other tenants to pay rent?',
        answer: 'Currently, you can only set up one tenant per property.',
      },
      {
        question: 'How does my tenant get set up?',
        answer: `After verifying your information, we’ll send your tenant an email
            with payment details. Your tenant will need to sign up to pay rent online
            and add their payment information.`,
      },
    ],
    IDENTITY_VERIFICATION_PAGE: [
      {
        question: 'Why do I need to verify my identity?',
        answer: `Federal regulations require us to meet all KYC (Know Your Customer) obligations before
            we can transfer money between accounts. Our online verification form verifies your identity so we
            can prevent fraud and comply with regulations.
            We do not sell or rent Personal Data to marketers or unaffiliated third parties.`,
      },
    ],
    PHONE_VERIFICATION_PAGE: [
      {
        question: 'What if I never got a code?',
        answer: (
          <>
            Please&nbsp;
            <Anchor href={StringConstants.LINKS.SUPPORT_PAGE.LANDLORD} target="_blank">
              contact us
            </Anchor>
            , and we&apos;ll be happy to help sort this out.
          </>
        ),
      },
    ],
    MOVE_IN_COSTS: {
      SECURITY_DEPOSIT_PAGE: [
        {
          question: 'How much can I charge for a security deposit?',
          answer: `In general, a security deposit should not exceed more than 3X the monthly rent.
             Check your local rental laws, too, since they may limit how much you can charge for a security deposit.`,
        },
        {
          question: 'Do I need to pay interest on the security deposit?',
          answer: `Possibly. Check your state and local laws since they often require interest to accrue while the
            deposit is held. The renter then collects that interest when they move out.`,
        },
        {
          question: 'Is there a limit on how much I can request for move-in costs?',
          answer: (
            <>
              <Text css={{ display: 'inline-block', marginBottom: 'token(spacing.sm)' }}>
                Yes. Due to federal regulations, the most you can request for move-in costs is $10,000 total.
              </Text>

              <Text css={{ display: 'inline-block' }}>
                Once you complete your account setup, you can request additional payments for rent-related expenses.
              </Text>
            </>
          ),
        },
      ],
      PRORATED_RENT_PAGE: [
        {
          question: 'How is this different from my base monthly rent?',
          answer: `Prorated rent is part of a month. If your renter doesn't move in on their monthly rent payment
            date, you may charge them prorated rent until they make their first full rent payment. You'll set up your
            ongoing base monthly rent payments later.`,
        },
        {
          question: 'How do I calculate my prorated rent?',
          answer: `First calculate the daily rent, which is the monthly rent divided by the total number of days
                in the month you're prorating. Next, multiply that daily rent by the number of days between your
                renter's move-in date and when they'll make their first full rent payment.`,
        },
        {
          question: 'Is there a limit on how much I can request for move-in costs?',
          answer: (
            <>
              <Text css={{ display: 'inline-block', marginBottom: 'token(spacing.sm)' }}>
                Yes. Due to federal regulations, the most you can request for move-in costs is $10,000 total.
              </Text>

              <Text css={{ display: 'inline-block' }}>
                Once you complete your account setup, you can request additional payments for rent-related expenses.
              </Text>
            </>
          ),
        },
      ],
      OTHER_PAGE: [
        {
          question: 'What are some examples of other move-in costs?',
          answer: `You may charge non-refundable fees for things like keeping a pet, using an onsite gym or
                reserving a parking space. Unlike monthly rent payments, you request these payments one at a time.`,
        },
        {
          question: 'Is there a limit on how much I can request for move-in costs?',
          answer: (
            <>
              <Text css={{ display: 'inline-block', marginBottom: 'token(spacing.sm)' }}>
                Yes. Due to federal regulations, the most you can request for move-in costs is $10,000 total.
              </Text>
              <br />
              <Text css={{ display: 'inline-block' }}>
                Once you complete your account setup, you can request additional payments for rent-related expenses.
              </Text>
            </>
          ),
        },
      ],
      DUE_DATE_PAGE: [
        {
          question: "What if I'm asking for move-in payments that aren't due on the same day?",
          answer: `Right now, our service requires all move-in costs to be due on the same day. As an option, you
                 can request one-time payments from your renter once you're setup. Note, though, that one-time payments
                 cannot be given a payment date. They're simply marked as outstanding until they're paid.`,
        },
        {
          question: 'How long does it take for payments to get deposited into my account?',
          answer: `Typically 3–5 business days since a secure banking network must validate and verify each
                transfer. Similar to depositing a check, that process takes a few days, and weekends and holidays can
                extend the time. Zillow does not access the funds as they are being transferred, and we ensure that our
                trusted processing partners employ strong security standards.`,
        },
      ],
    },
  },
  DASHBOARD_V2: {
    EDIT_PAYMENT_LENGTH_PAGE: [
      {
        question: 'When can I change the end date or stop collecting payments?',
        answer: `You can change the end date or stop collecting payments anytime,
            but you cannot stop a payment that your renter has already sent.`,
      },
    ],
    EDIT_RENT_AMOUNT_CHANGE_PAGE: (nextAllowedChangeDate) => [
      {
        question: `What if my tenant agreed to have the change take effect
            before ${nextAllowedChangeDate}?`,
        answer: `Since your tenant's earlier rent payments are already paid
            or in progress, please collect any outstanding balance offline.`,
      },
    ],
    ONE_TIME_PAYMENT_PAGE: [
      {
        question: 'How is this different from a rent payment?',
        answer: `These payments are meant to be for other expenses covered by your lease, such as utility
            costs. They're separate from rent, and must be requested individually.`,
      },
      {
        question: "Why can't I request one-time payments as often and for as much as I'd like?",
        answer: `One-time payment requests should only be for expenses covered by your lease. Placing
            limits on them is one way that we strive to avoid improper use of our service.`,
      },
    ],
    EDIT_ACCOUNT_INFO_PAGE: [
      {
        question: 'Why did my deposit fail?',
        answer: `Check your payment account if the info recently changed.
                It may be possible to resolve this issue by re-entering your bank account credentials,
                or switching to a new deposit account.`,
      },
    ],
    EDIT_STRIPE_CONTACT_INFO_MODAL: [
      {
        question: 'Why do I need to update my contact information with Stripe?',
        answer: `As our payment processor, Stripe helps facilitate the proper and timely
                completion of 1099 tax forms for both your annual taxes and reporting to the IRS.
                In November, they will reach out to you directly to verify your tax related information.
                To ensure this process goes smoothly, we want to ensure they have the most up to date contact information for you.`,
      },
      {
        question: 'What is Stripe?',
        answer: `Zillow has partnered with Stripe as our payment processor.
                They power our ability to facilitate payments between tenants and landlords.
                Stripe also assists with tax documentation and financial services compliance.`,
      },
    ],
  },
};
